/**
 * Says so, on every page, when new documents can't be processed because the
 * subscription isn't in good standing (backend src/entitlement.py).
 *
 * Reads /billing/entitlement on load and every ten minutes, and reacts at once
 * to a 402 SUBSCRIPTION_INACTIVE from any upload or analyse call. Shows
 * nothing while things are fine, or when the status can't be read: a banner
 * must never appear because of a network blip.
 */
import { useEffect, useState } from 'react'
import { AlertTriangle, PauseCircle } from 'lucide-react'
import { billingApi } from '../services/api'

const REFRESH_MS = 10 * 60 * 1000
const SHOWN = new Set(['suspended', 'inactive', 'no_plan'])

export default function SubscriptionBanner() {
  const [entitlement, setEntitlement] = useState(null)

  useEffect(() => {
    let cancelled = false
    const load = () => billingApi.getEntitlement()
      .then(res => { if (!cancelled) setEntitlement(res.data || null) })
      .catch(() => {})
    load()
    const timer = setInterval(load, REFRESH_MS)
    const onBlocked = event => {
      const d = event.detail || {}
      setEntitlement({ status: d.status, can_process: false, message: d.error, reason: d.reason })
    }
    window.addEventListener('subscription-inactive', onBlocked)
    return () => {
      cancelled = true
      clearInterval(timer)
      window.removeEventListener('subscription-inactive', onBlocked)
    }
  }, [])

  if (!entitlement || !SHOWN.has(entitlement.status) || !entitlement.message) return null
  const blocked = entitlement.can_process === false
  const Icon = blocked ? PauseCircle : AlertTriangle

  return (
    <div
      role={blocked ? 'alert' : 'status'}
      data-testid="subscription-banner"
      className={`mb-4 flex items-start gap-3 rounded-lg border px-4 py-3 text-sm ${
        blocked
          ? 'border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/30 dark:text-red-200'
          : 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200'
      }`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <p>
        <strong>{blocked ? 'Processing paused. ' : 'Subscription needs attention. '}</strong>
        {entitlement.message}
      </p>
    </div>
  )
}
